import { forwardRef } from "react";

import { faArrowTurnDownLeft } from "@fortawesome/pro-regular-svg-icons";
import { twJoin } from "tailwind-merge";

import { Button } from "src/base-components/Button";

type ChatInputProps = {
  disabled: boolean;
  errored: boolean;
  minified: boolean;
  mentionPlaceholder?: string;
  submitLabel?: string;
  onSubmit: VoidFunction;
  onReset: VoidFunction;
  dataLoc?: string;
};

export const ChatInput = forwardRef<HTMLDivElement, ChatInputProps>(
  (
    {
      disabled,
      errored,
      minified,
      mentionPlaceholder,
      submitLabel,
      onSubmit,
      onReset,
      dataLoc,
    },
    ref,
  ) => {
    if (errored) {
      return (
        <div className="broder-gray-200 flex items-center justify-center rounded-lg border bg-gray-50 p-3.5">
          <Button
            iconRight={faArrowTurnDownLeft}
            size="sm"
            variant="primary"
            onClick={onReset}
          >
            Start a new chat
          </Button>
        </div>
      );
    }

    const resolvedSubmitLabel =
      submitLabel ?? (minified ? "Continue" : "Start Chat");

    return (
      <div className="broder-gray-200 relative flex flex-col rounded-lg border bg-gray-0 shadow-sm">
        <div
          ref={ref}
          className={twJoin(
            "resize-none",
            "border border-transparent",
            "pt-2 pb-2.5 pl-3",
            "font-inter-normal-13px text-gray-800",
            "focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/25",
            minified ? "rounded-lg" : "rounded-t-lg",
            minified ? "pr-24" : "pr-3",
          )}
          data-loc={dataLoc ?? "chat-input"}
        />
        {minified && (
          <div className="absolute top-2 right-2">
            <Button
              disabled={disabled}
              htmlType="submit"
              iconRight={faArrowTurnDownLeft}
              size="sm"
              variant="primary"
              onClick={onSubmit}
            >
              {resolvedSubmitLabel}
            </Button>
          </div>
        )}
        {!minified && (
          <div className="mx-3 flex items-center justify-between border-t border-t-gray-100 py-2">
            {mentionPlaceholder && (
              <p className="font-inter-normal-12px text-gray-500">
                {mentionPlaceholder}
              </p>
            )}
            <Button
              dataLoc="chat-start-button"
              disabled={disabled}
              htmlType="submit"
              iconRight={faArrowTurnDownLeft}
              size="sm"
              variant="primary"
              onClick={onSubmit}
            >
              {resolvedSubmitLabel}
            </Button>
          </div>
        )}
      </div>
    );
  },
);
